import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { lcFlow } from '../../help/flows';
import { EXP_FORM_STAGES } from '../../help/statusStages';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { ExpFormService } from '../../services/expForm.service';
import { ExportLcService } from '../../services/exportLc.service';
import { BuyerService } from '../../services/buyer.service';
import { PERMISSIONS } from '../../constants/permissions';
import { ExpRealizationStatus, ExportLcListItem, IBookingRateSuggestion, ICreateExpFormRequest, IExpForm } from '../../interfaces/commercial.interface';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import BuyerAutocomplete from '../merchandising/BuyerAutocomplete';
import ExpRealizationPanel from './ExpRealizationPanel';
import RefAutocomplete, { RefOption } from './common/RefAutocomplete';
import { fetchAllPages } from './common/fetchAllPages';
import { enumLabel } from '../../utils/garmentsLabels';
import AttachmentsPanel from '../common/AttachmentsPanel'; // W3-K

const fmtMoney = (n: number | null | undefined) =>
    (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY: ICreateExpFormRequest = {
    expNumber: '', expDate: '', exportLcId: null, buyerId: null, buyerName: '', bankName: '', bankBranch: '',
    currencyCode: 'USD', invoiceValue: 0, shipmentDate: null, blNumber: '', prcNumber: '', prcDate: null, remarks: '',
    bookingRate: null,
};

const STATUS_COLOR: Record<ExpRealizationStatus, 'default' | 'info' | 'success' | 'warning'> = {
    NOT_REALIZED: 'default', PARTIALLY_REALIZED: 'info', REALIZED: 'success', WRITTEN_OFF: 'warning',
};

/** Create/edit the EXP form header. When editing an existing EXP this page also hosts the
 * realization panel below the header, matching the sibling UD detail/lines split. */
const ExpFormForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id && id !== 'new';
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [form, setForm] = useState<ICreateExpFormRequest>(EMPTY);
    const [exp, setExp] = useState<IExpForm | null>(null);
    const [buyer, setBuyer] = useState<IBuyer | null>(null);
    const [exportLcOptions, setExportLcOptions] = useState<RefOption[]>([]);
    const [exportLcLoading, setExportLcLoading] = useState(false);
    const [exportLcRows, setExportLcRows] = useState<ExportLcListItem[]>([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    // Booking rate (BDT per 1 unit of the EXP's currency): prompted on create and on a late booking.
    const [suggestion, setSuggestion] = useState<IBookingRateSuggestion | null>(null);
    const [rateTouched, setRateTouched] = useState(false);
    const [bookRate, setBookRate] = useState<number | ''>('');
    const [booking, setBooking] = useState(false);
    const [bookPostingDate, setBookPostingDate] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setExportLcLoading(true);
            try {
                const exportLcs = await fetchAllPages((page, size) => ExportLcService.list(institutionId, { page, size }));
                if (!cancelled) {
                    setExportLcRows(exportLcs);
                    setExportLcOptions(exportLcs.map(lc => ({ id: lc.id, label: lc.lcNumber })));
                }
            } catch (e) {
                if (!cancelled) toast.showApiError(e, 'Failed to load export LC options');
            } finally {
                if (!cancelled) setExportLcLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    // Default suggestion = the latest rate booked/realised for this currency; never overwrites a typed value.
    const rateCurrency = isEdit ? exp?.currencyCode : form.currencyCode;
    const needsRatePrompt = !isEdit || (!!exp && !exp.receivableBooked);
    useEffect(() => {
        if (!needsRatePrompt || !rateCurrency || rateCurrency.length !== 3 || !canManage) return;
        let cancelled = false;
        (async () => {
            try {
                const s = await ExpFormService.suggestBookingRate(institutionId, rateCurrency);
                if (cancelled) return;
                setSuggestion(s);
                if (s.rate != null && !rateTouched) {
                    if (isEdit) setBookRate(s.rate);
                    else setForm(f => (f.bookingRate == null || f.bookingRate === 0) ? { ...f, bookingRate: s.rate } : f);
                }
            } catch {
                if (!cancelled) setSuggestion(null);   // a missing suggestion must never block the form
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rateCurrency, needsRatePrompt, institutionId, canManage]);

    const load = async () => {
        try {
            const res = await ExpFormService.get(institutionId, id!);
            setExp(res);
            setForm({
                expNumber: res.expNumber, expDate: res.expDate, exportLcId: res.exportLcId,
                salesOrderId: res.salesOrderId, salesOrderRef: res.salesOrderRef, buyerId: res.buyerId,
                buyerName: res.buyerName, bankName: res.bankName, bankBranch: res.bankBranch ?? '',
                currencyCode: res.currencyCode, invoiceValue: res.invoiceValue, shipmentDate: res.shipmentDate,
                blNumber: res.blNumber ?? '', prcNumber: res.prcNumber ?? '', prcDate: res.prcDate, remarks: res.remarks ?? '',
            });
            if (res.buyerId) {
                try {
                    setBuyer(await BuyerService.getById(institutionId, res.buyerId));
                } catch (e) {
                    toast.showApiError(e, 'Failed to load linked buyer');
                }
            } else {
                setBuyer(null);
            }
        } catch (e) {
            toast.showApiError(e, 'Failed to load EXP form');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isEdit) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const isBdtCreate = form.currencyCode.trim() === 'BDT';
    const rateOk = isEdit || isBdtCreate || (form.bookingRate != null && form.bookingRate > 0);
    const valid = form.expNumber.trim().length > 0 && !!form.expDate && form.buyerName.trim().length > 0
        && form.bankName.trim().length > 0 && form.currencyCode.trim().length === 3 && form.invoiceValue >= 0.01
        && rateOk;

    const saveWhy = saving ? null
        : !form.expNumber.trim() ? 'Enter the EXP number.'
        : !form.expDate ? 'Enter the EXP date.'
        : !form.buyerName.trim() ? 'Enter the buyer name.'
        : !form.bankName.trim() ? 'Enter the bank name.'
        : form.currencyCode.trim().length !== 3 ? 'Enter a 3-letter currency code.'
        : form.invoiceValue < 0.01 ? 'Enter an invoice value of at least 0.01.'
        : !rateOk ? 'Enter a booking rate above zero.'
        : null;

    // View-only roles can reach this page (it doubles as the edit form under EXP_VIEW, App.tsx),
    // so render every field read-only and hide Save when the caller lacks EXP_MANAGE.
    const readOnly = !canManage;

    const handleSave = async () => {
        // Submit trimmed values -- Save is gated on trimmed length, so an untrimmed value here
        // would silently persist leading/trailing whitespace the backend's @NotBlank never catches
        // (it checks trimmed length too).
        const payload: ICreateExpFormRequest = {
            ...form,
            expNumber: form.expNumber.trim(),
            buyerName: form.buyerName.trim(),
            bankName: form.bankName.trim(),
            bankBranch: form.bankBranch?.trim() || null,
            blNumber: form.blNumber?.trim() || null,
            prcNumber: form.prcNumber?.trim() || null,
            remarks: form.remarks?.trim() || null,
            bookingRate: isBdtCreate ? 1 : form.bookingRate,
        };
        try {
            setSaving(true);
            if (isEdit) {
                const updated = await ExpFormService.update(institutionId, id!, { ...payload });
                toast.showSuccess('EXP form updated');
                setExp(updated);
            } else {
                const created = await ExpFormService.create(institutionId, payload);
                if (created.postingWarning) toast.showWarning(created.postingWarning);
                else toast.showSuccess('EXP form created and export receivable booked');
                navigate(`/commercial/exp-form/${created.id}`);
            }
        } catch (e) {
            toast.showApiError(e, 'Failed to save EXP form');
        } finally {
            setSaving(false);
        }
    };

    const handleBook = async () => {
        const rate = exp?.currencyCode === 'BDT' ? 1 : Number(bookRate);
        if (!exp || !(rate > 0)) return;
        try {
            setBooking(true);
            const booked = await ExpFormService.bookReceivable(institutionId, exp.id,
                { bookingRate: rate, postingDate: bookPostingDate || null });
            setExp(booked);
            toast.showSuccess('Export receivable booked');
        } catch (e) {
            toast.showApiError(e, 'Failed to book the export receivable');
        } finally {
            setBooking(false);
        }
    };

    const suggestionHint = suggestion && suggestion.rate != null
        ? `Last used: ${suggestion.rate}${suggestion.source === 'LAST_REALIZATION' ? ' (from a realization)' : ''}`
        : undefined;

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 3, maxWidth: 900 }} data-help-page="commercial.expForm.page">
            {exp && <FlowStrip data-help="commercial.exp.flow" stages={lcFlow({ current: 'exp', lc: { id: exp.exportLcId }, exp: { no: exp.expNumber } })} />}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{isEdit ? `EXP ${form.expNumber}` : 'New EXP Form'}</Typography>
                {exp && <StatusStagesChip size="medium" data-help="commercial.exp.statusStages" label={enumLabel(exp.realizationStatus)} status={exp.realizationStatus} stages={EXP_FORM_STAGES} color={STATUS_COLOR[exp.realizationStatus]} />}
            </Box>

            <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="commercial.expForm.section.details">
                <TextField data-help="commercial.expForm.field.expNumber" label="EXP Number" required value={form.expNumber} disabled={readOnly}
                    onChange={e => setForm({ ...form, expNumber: e.target.value })} />
                <TextField data-help="commercial.expForm.field.expDate" label="EXP Date" type="date" InputLabelProps={{ shrink: true }} required
                    disabled={readOnly}
                    value={form.expDate} onChange={e => setForm({ ...form, expDate: e.target.value })} />
                <TextField data-help="commercial.expForm.field.buyerName" label="Buyer Name" required value={form.buyerName} disabled={readOnly}
                    onChange={e => setForm({ ...form, buyerName: e.target.value })} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: '1 1 260px', maxWidth: 360 }}>
                        <RefAutocomplete
                            data-help="commercial.expForm.field.exportLc"
                            label="Export LC (optional)"
                            options={exportLcOptions}
                            loading={exportLcLoading}
                            disabled={readOnly}
                            value={form.exportLcId ?? null}
                            onChange={newId => {
                                // New EXP form: carry the LC's buyer and currency over so the two cannot
                                // silently disagree (the currency otherwise stays at the USD default).
                                const lc = isEdit ? undefined : exportLcRows.find(r => r.id === newId);
                                setForm(f => ({
                                    ...f,
                                    exportLcId: newId,
                                    ...(lc ? {
                                        buyerName: f.buyerName.trim() ? f.buyerName : (lc.buyerName ?? ''),
                                        currencyCode: lc.currencyCode || f.currencyCode,
                                    } : {}),
                                }));
                            }}
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 260px', maxWidth: 360 }}>
                        <BuyerAutocomplete
                            data-help="commercial.expForm.field.buyer"
                            label="Buyer (optional)"
                            value={buyer}
                            disabled={readOnly}
                            onChange={newBuyer => {
                                setBuyer(newBuyer);
                                setForm(f => ({ ...f, buyerId: newBuyer?.id ?? null }));
                            }}
                        />
                    </Box>
                </Box>
                <TextField data-help="commercial.expForm.field.bankName" label="Bank Name" required value={form.bankName} disabled={readOnly}
                    onChange={e => setForm({ ...form, bankName: e.target.value })} />
                <TextField data-help="commercial.expForm.field.bankBranch" label="Bank Branch" value={form.bankBranch ?? ''} disabled={readOnly}
                    onChange={e => setForm({ ...form, bankBranch: e.target.value })} />
                <TextField data-help="commercial.expForm.field.currency" label="Currency Code" required value={form.currencyCode}
                    disabled={isEdit || readOnly}
                    helperText={isEdit ? 'The currency cannot be changed after the EXP is created.' : undefined}
                    onChange={e => setForm({ ...form, currencyCode: e.target.value.toUpperCase() })} />
                <TextField data-help="commercial.expForm.field.invoiceValue" label="Invoice Value" type="number" required value={form.invoiceValue}
                    disabled={readOnly || !!exp?.receivableBooked}
                    helperText={exp?.receivableBooked
                        ? 'Locked: the receivable is booked to the ledger. Delete the EXP (reverses the booking) to change it.'
                        : undefined}
                    onChange={e => setForm({ ...form, invoiceValue: Number(e.target.value) })} />
                {!isEdit && (
                    <TextField data-help="commercial.expForm.field.bookingRate" label={`Booking rate (BDT per 1 ${form.currencyCode || 'unit'})`} type="number"
                        required={!isBdtCreate} value={isBdtCreate ? 1 : (form.bookingRate ?? '')}
                        disabled={readOnly || isBdtCreate}
                        inputProps={{ 'data-testid': 'exp-booking-rate', min: 0, step: 'any' }}
                        helperText={isBdtCreate
                            ? 'Fixed at 1 for a BDT EXP'
                            : (suggestionHint ?? 'Rate at shipment. Revenue and the receivable are booked in BDT at this rate.')}
                        onChange={e => {
                            setRateTouched(true);
                            setForm({ ...form, bookingRate: e.target.value === '' ? null : Number(e.target.value) });
                        }} />
                )}
                <TextField data-help="commercial.expForm.field.shipmentDate" label="Shipment Date" type="date" InputLabelProps={{ shrink: true }} value={form.shipmentDate ?? ''}
                    disabled={readOnly}
                    onChange={e => setForm({ ...form, shipmentDate: e.target.value || null })} />
                <TextField data-help="commercial.expForm.field.blNumber" label="B/L Number" value={form.blNumber ?? ''} disabled={readOnly}
                    onChange={e => setForm({ ...form, blNumber: e.target.value })} />
                <TextField data-help="commercial.expForm.field.prcNumber" label="PRC Number (optional)" value={form.prcNumber ?? ''} disabled={readOnly}
                    onChange={e => setForm({ ...form, prcNumber: e.target.value })} />
                <TextField data-help="commercial.expForm.field.prcDate" label="PRC Date (optional)" type="date" InputLabelProps={{ shrink: true }}
                    value={form.prcDate ?? ''} disabled={readOnly}
                    onChange={e => setForm({ ...form, prcDate: e.target.value || null })} />
                <TextField data-help="commercial.expForm.field.remarks" label="Remarks" multiline minRows={2} value={form.remarks ?? ''} disabled={readOnly}
                    onChange={e => setForm({ ...form, remarks: e.target.value })} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button data-help="commercial.expForm.cancel" onClick={() => navigate(-1)}>{readOnly ? 'Back' : 'Cancel'}</Button>
                    {!readOnly && (
                        <Button data-help="commercial.expForm.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={saving || !valid} onClick={handleSave}>Save</Button>
                    )}
                </Box>
            </Paper>

            {isEdit && exp && (exp.receivableBooked || !(Number(exp.realizedAmount) > 0)) && (
                <Paper sx={{ p: 2, mt: 3 }} data-testid="exp-receivable-card" data-help-section="commercial.expForm.receivable">
                    <Typography variant="h6" sx={{ mb: 1 }}>Export receivable</Typography>
                    {exp.receivableBooked ? (
                        <Typography variant="body2">
                            Booked to the ledger: {exp.currencyCode} {fmtMoney(exp.invoiceValue)} at {exp.bookingRate} =
                            {' '}BDT {fmtMoney(exp.bookedBaseAmount)} (Dr Export Receivable / Cr Export Sales).
                        </Typography>
                    ) : (
                        <>
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                No receivable is booked for this EXP, so its revenue is recognised only when cash is
                                realised. {canManage ? 'Enter the shipment rate to book it now (not possible once a realization exists).' : ''}
                            </Alert>
                            {canManage && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                                    <Box sx={{ flex: '1 1 220px', maxWidth: 300 }}>
                                        <TextField data-help="commercial.expForm.field.receivableRate" label={`Booking rate (BDT per 1 ${exp.currencyCode})`} type="number" size="small"
                                            fullWidth value={exp.currencyCode === 'BDT' ? 1 : bookRate}
                                            disabled={exp.currencyCode === 'BDT'}
                                            inputProps={{ 'data-testid': 'exp-book-rate', min: 0, step: 'any' }}
                                            helperText={suggestionHint}
                                            onChange={e => { setRateTouched(true); setBookRate(e.target.value === '' ? '' : Number(e.target.value)); }} />
                                    </Box>
                                    <Box sx={{ flex: '1 1 200px', maxWidth: 260 }}>
                                        <TextField data-help="commercial.expForm.field.postingDate" label="Posting date (optional)" type="date" size="small" fullWidth
                                            value={bookPostingDate} InputLabelProps={{ shrink: true }}
                                            inputProps={{ 'data-testid': 'exp-book-posting-date' }}
                                            helperText="Leave empty to post on the shipment date"
                                            onChange={e => setBookPostingDate(e.target.value)} />
                                    </Box>
                                    <Button data-help="commercial.expForm.bookReceivable" variant="outlined" disabled={booking || (exp.currencyCode !== 'BDT' && !(Number(bookRate) > 0))}
                                        onClick={handleBook}>
                                        Book receivable
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </Paper>
            )}

            {isEdit && exp && (
                <ExpRealizationPanel institutionId={institutionId} exp={exp} onChanged={load} />
            )}
            {isEdit && id && <AttachmentsPanel entityType="EXP_FORM" entityId={id} canManage={canManage} />} {/* W3-K */}
        </Box>
    );
};

export default ExpFormForm;
